import { usePrivilege } from '@mews-commander/core';
import {
    GeneralPortfolioProductSubscriptionType,
    SsoConnectionDetails,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { Link } from '@mews-commander/ui';
import { SystemWideBanner } from '@mews/b2b-ui';
import { DateTimeService, LocalizedText, useTimezone } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';

export function ExpiredSsoBanner() {
    const timezone = useTimezone();
    const isAdmin = usePrivilege('admin');
    const { portfolioProductSubscriptionTypes, id } = useCurrentEnterprise();
    const hasSsoAccess =
        isAdmin && !!portfolioProductSubscriptionTypes?.includes(GeneralPortfolioProductSubscriptionType.SingleSignOn);

    const { data: ssoConnection } = useRepositoryQuery(
        'ssoConnection',
        'get',
        {},
        { enabled: hasSsoAccess, meta: { disableDefaultErrorHandler: true } }
    );
    const daysUntilExpiration = getDaysUntilSsoExpirationDate(ssoConnection, timezone);

    if (!hasSsoAccess || !daysUntilExpiration || daysUntilExpiration >= 7) {
        return null;
    }

    return (
        <SystemWideBanner
            variant="warning"
            importance="urgent"
            text={
                <Stack distribution="space-between" itemAlignment="center" fill>
                    {daysUntilExpiration <= 0 ? (
                        <LocalizedText textKey={TextKey.ExpiredSsoBannerExpiredHeading} />
                    ) : (
                        <LocalizedText
                            textKey={TextKey.ExpiredSsoBannerReminderHeading}
                            substitutes={{ daysRemaining: daysUntilExpiration }}
                        />
                    )}
                    <Link
                        variant="basic"
                        to="/$currentEnterpriseId/Security/Index"
                        params={{ currentEnterpriseId: id }}
                    >
                        <LocalizedText textKey={TextKey.ExpiredSsoBannerUpdateSecretLink} />
                    </Link>
                </Stack>
            }
            showIcon
        />
    );
}

function getDaysUntilSsoExpirationDate(data: SsoConnectionDetails | undefined, timezone: string) {
    if (!data || !data.clientSecretExpirationDate) {
        return undefined;
    }

    const today = DateTimeService.now(timezone);
    const expirationDate = DateTimeService.fromUtcString(data.clientSecretExpirationDate, timezone);

    return DateTimeService.diffDays(expirationDate, today);
}
